import axios from 'axios'
import type {
  DeliveryConnection,
  DeliveryPlatform,
  DeliveryReview,
  DeliveryReviewFilters,
  DeliveryStoreMapping,
  Paginated
} from '@/types/deliveryReviews'

const REVIEWS_PER_PAGE = 10

const base = (merchantId: number) => `/merchants/${merchantId}/delivery-reviews`

export async function fetchConnections(merchantId: number): Promise<DeliveryConnection[]> {
  const { data } = await axios.get(`${base(merchantId)}/connections`)
  return data.data
}

/** Creates the merchant's dedicated invitation address for the platform (idempotent). */
export async function startConnection(merchantId: number, platform: DeliveryPlatform): Promise<DeliveryConnection> {
  const { data } = await axios.post(`${base(merchantId)}/connections/${platform}`)
  return data.data
}

/** Re-checks which platform stores the merchant gave Pongo access to. */
export async function syncConnection(merchantId: number, platform: DeliveryPlatform): Promise<DeliveryConnection> {
  const { data } = await axios.post(`${base(merchantId)}/connections/${platform}/sync`)
  return data.data
}

export async function saveStoreMappings(
  merchantId: number,
  platform: DeliveryPlatform,
  mappings: DeliveryStoreMapping[]
): Promise<DeliveryConnection> {
  const { data } = await axios.put(`${base(merchantId)}/connections/${platform}/stores`, { mappings })
  return data.data
}

/**
 * Why a platform store's mapping was refused:
 * - `notGranted`: the platform store is no longer shared with Pongo (`mappings.N.external_store_id`)
 * - `storeInvalid`: the Pongo store is not the merchant's or is already linked (`mappings.N.store_id`)
 */
export type StoreMappingRowError = 'notGranted' | 'storeInvalid'

/**
 * Reads a 422 from `PUT …/connections/{platform}/stores` (Laravel `errors` bag keyed `mappings.N.field`,
 * N being the index in the sent `mappings`) into one error per platform store.
 * Returns null when the error is not a validation error.
 */
export function storeMappingErrors(
  error: unknown,
  mappings: DeliveryStoreMapping[]
): Record<string, StoreMappingRowError> | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 422) return null

  const bag: Record<string, unknown> = error.response.data?.errors ?? {}
  const rows: Record<string, StoreMappingRowError> = {}
  for (const key of Object.keys(bag)) {
    const match = /^mappings\.(\d+)\.(external_store_id|store_id)$/.exec(key)
    const mapping = match ? mappings[Number(match[1])] : undefined
    if (!match || !mapping) continue
    if (match[2] === 'external_store_id' || !rows[mapping.external_store_id]) {
      rows[mapping.external_store_id] = match[2] === 'external_store_id' ? 'notGranted' : 'storeInvalid'
    }
  }
  return rows
}

export async function fetchReviews(
  merchantId: number,
  filters: DeliveryReviewFilters,
  page: number
): Promise<Paginated<DeliveryReview>> {
  const params: Record<string, string | number> = { page, per_page: REVIEWS_PER_PAGE }
  if (filters.platform) params.platform = filters.platform
  if (filters.storeId) params.store_id = filters.storeId
  if (filters.rating) params.rating = filters.rating
  if (filters.onlyUnanswered) params.replied = 0

  const { data } = await axios.get(`${base(merchantId)}/reviews`, { params })
  return data
}

export async function replyToReview(merchantId: number, reviewId: number, response: string): Promise<DeliveryReview> {
  const { data } = await axios.post(`${base(merchantId)}/reviews/${reviewId}/reply`, { response })
  return data.data
}

export async function suggestReply(merchantId: number, reviewId: number): Promise<string> {
  const { data } = await axios.post(`${base(merchantId)}/reviews/${reviewId}/ai-reply`)
  return data.data?.text ?? ''
}

/**
 * Seconds to wait when the API throttled the request (429); null otherwise. CORS hides `Retry-After`
 * from the browser unless exposed, hence the fallback on the 1-minute throttle window.
 */
export function retryAfterSeconds(error: unknown): number | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 429) return null
  const seconds = Number(error.response.headers?.['retry-after'])
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 60
}

/** Message the API attached to a failed request, if any. */
export function apiErrorMessage(error: unknown): string | null {
  return axios.isAxiosError(error) ? (error.response?.data?.message ?? null) : null
}
